import React, { useEffect, useState } from 'react';
import { Modal, Select, Alert, Spin, message } from 'antd';
import partnerNetworkService from '../../services/partnerNetworkService';

export default function ReassignRmPartnersModal({
  open,
  rmUser,
  onCancel,
  onReassigned,
}) {
  const [partnerCount, setPartnerCount] = useState(0);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [rms, setRms] = useState([]);
  const [loadingRms, setLoadingRms] = useState(false);
  const [toRmId, setToRmId] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);

  const fromRmId = rmUser?.id ?? rmUser?.uuid;

  useEffect(() => {
    if (!open || !fromRmId) return;

    let cancelled = false;

    const load = async () => {
      setLoadingPartners(true);
      setLoadingRms(true);
      setToRmId(undefined);
      setPartnerCount(0);

      try {
        const [partners, eligible] = await Promise.all([
          partnerNetworkService.getPartnersByRm(fromRmId),
          partnerNetworkService.getEligibleRms(),
        ]);

        if (cancelled) return;

        setPartnerCount(Array.isArray(partners) ? partners.length : 0);
        const list = (Array.isArray(eligible) ? eligible : []).filter(
          (r) => r.id !== fromRmId
        );
        setRms(list);
      } catch (e) {
        if (cancelled) return;
        message.error(e?.response?.data?.message || e?.message || 'Failed to load partner mappings');
      } finally {
        if (!cancelled) {
          setLoadingPartners(false);
          setLoadingRms(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, fromRmId]);

  const handleOk = async () => {
    if (!toRmId) {
      message.warning('Select the RM who will take over these partners');
      return;
    }
    if (!fromRmId) return;

    setSubmitting(true);
    try {
      const res = await partnerNetworkService.bulkReassignPartners(fromRmId, toRmId);
      if (res?.success === false) {
        message.error(res?.message || 'Failed to reassign partners');
        return;
      }
      message.success(
        res?.message || `${res?.data?.partners_reassigned ?? partnerCount} partner(s) reassigned`
      );
      onReassigned?.();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to reassign partners');
    } finally {
      setSubmitting(false);
    }
  };

  const rmName = rmUser?.name || 'this user';

  return (
    <Modal
      title="Reassign partners before deactivation"
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText="Reassign partners"
      confirmLoading={submitting}
      okButtonProps={{ disabled: loadingPartners || loadingRms || partnerCount === 0 }}
      destroyOnHidden
    >
      {loadingPartners ? (
        <div className="py-6 flex justify-center">
          <Spin />
        </div>
      ) : (
        <>
          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message={`${rmName} has ${partnerCount} mapped partner(s). Assign them to another RM before deactivating this account.`}
          />
          <div className="text-sm text-gray-600 mb-2">New RM</div>
          <Select
            className="w-full"
            placeholder={loadingRms ? 'Loading RMs…' : 'Select RM'}
            loading={loadingRms}
            value={toRmId}
            onChange={setToRmId}
            showSearch
            optionFilterProp="label"
            options={rms.map((r) => ({
              label: r.name,
              value: r.id,
            }))}
          />
          <p className="text-xs text-gray-500 mt-3">
            Historical orders keep the RM recorded at order time; only future partner visibility
            changes.
          </p>
        </>
      )}
    </Modal>
  );
}
